// src/platform/pwa/notificationManager.js

import {
  eventBus,
  logger
} from '@/platform/core'

class NotificationManager {
  constructor() {
    this.notifications = []
    this.nextId = 1
    this.timers = new Map()

    this.initialized = false
  }

  init() {
    if (this.initialized) {
      return
    }

    this.initialized = true

    logger.info(
      'Notification manager initialized.'
    )

    eventBus.emit(
      'platform.notification.ready',
      {
        timestamp:
          new Date().toISOString()
      }
    )
  }

  destroy() {
    for (const timer of this.timers.values()) {
      window.clearTimeout(timer)
    }

    this.timers.clear()
    this.notifications = []
    this.initialized = false

    logger.info(
      'Notification manager destroyed.'
    )
  }

  notify(options = {}) {
    const notification = {
      id: this.nextId++,

      type:
        options.type || 'info',

      title:
        options.title || '',

      message:
        options.message || '',

      persistent:
        Boolean(options.persistent),

      timeout:
        typeof options.timeout === 'number'
          ? options.timeout
          : 5000,

      action:
        options.action || null,

      metadata:
        options.metadata || null,

      createdAt:
        new Date().toISOString()
    }

    this.notifications.push(notification)

    logger.debug(
      'Notification created.',
      {
        id: notification.id,
        type: notification.type,
        title: notification.title
      }
    )

    eventBus.emit(
      'platform.notification.added',
      {
        notification
      }
    )

    if (
      !notification.persistent &&
      notification.timeout > 0 &&
      typeof window !== 'undefined'
    ) {
      const timer =
        window.setTimeout(() => {
          this.dismiss(notification.id)
        }, notification.timeout)

      this.timers.set(
        notification.id,
        timer
      )
    }

    return notification
  }

  info(message, options = {}) {
    return this.notify({
      ...options,
      type: 'info',
      message
    })
  }

  success(message, options = {}) {
    return this.notify({
      ...options,
      type: 'success',
      message
    })
  }

  warning(message, options = {}) {
    return this.notify({
      ...options,
      type: 'warning',
      message
    })
  }

  error(message, options = {}) {
    return this.notify({
      ...options,

      type: 'error',

      message,

      persistent:
        options.persistent !== undefined
          ? options.persistent
          : true
    })
  }

  dismiss(id) {
    const index =
      this.notifications.findIndex(
        notification =>
          notification.id === id
      )

    if (index === -1) {
      return false
    }

    const timer =
      this.timers.get(id)

    if (timer) {
      window.clearTimeout(timer)
      this.timers.delete(id)
    }

    const [notification] =
      this.notifications.splice(
        index,
        1
      )

    eventBus.emit(
      'platform.notification.dismissed',
      {
        notification
      }
    )

    return true
  }

  clear() {
    for (const timer of this.timers.values()) {
      window.clearTimeout(timer)
    }

    this.timers.clear()

    const notifications = [
      ...this.notifications
    ]

    this.notifications = []

    eventBus.emit(
      'platform.notification.cleared',
      {
        notifications
      }
    )

    return notifications.length
  }

  getAll() {
    return [
      ...this.notifications
    ]
  }

  getById(id) {
    return (
      this.notifications.find(
        notification =>
          notification.id === id
      ) || null
    )
  }

  count() {
    return this.notifications.length
  }

  hasNotifications() {
    return (
      this.notifications.length > 0
    )
  }

  async executeAction(id) {
    const notification =
      this.getById(id)

    if (!notification?.action) {
      return false
    }

    const handler =
      notification.action.handler

    if (typeof handler !== 'function') {
      logger.warn(
        'Notification action has no valid handler.',
        {
          id
        }
      )

      return false
    }

    try {
      eventBus.emit(
        'platform.notification.action.started',
        {
          notification
        }
      )

      await handler(notification)

      eventBus.emit(
        'platform.notification.action.completed',
        {
          notification
        }
      )

      return true
    } catch (error) {
      logger.error(
        'Notification action failed.',
        {
          id,
          error
        }
      )

      eventBus.emit(
        'platform.notification.action.error',
        {
          notification,
          error
        }
      )

      return false
    }
  }
}

const notificationManager =
  new NotificationManager()

export default notificationManager